import { useCallback } from 'react';

import { AssignReviewerFormFields, type AssignReviewerFormData } from '../validation';

import type { User } from '@/types/directory/User';
import type { Reviewee } from '@/utils/reviewEvaluatorSelection';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import { MAX_NODES } from '@/components/ReviewerNodes/ReviewerNodes';
import { useAmelioTranslations } from '@/hooks';
import { showWarningToast } from '@/plugins/Toast';

interface ReviewerAssignmentsParams {
    baseReviewees: Reviewee[];
    getValues: UseFormGetValues<AssignReviewerFormData>;
    setValue: UseFormSetValue<AssignReviewerFormData>;
    /** The reviewers changed: why "Next" refused them no longer holds. */
    onChange: () => void;
}

/** Adds and removes reviewers in the form only: the server hears of them on "Next". */
export const useReviewerAssignments = ({
    baseReviewees,
    getValues,
    setValue,
    onChange,
}: ReviewerAssignmentsParams) => {
    const { t } = useAmelioTranslations();

    const saveLocally = useCallback(
        (assignments: AssignReviewerFormData['reviewerAssignments']) => {
            setValue(AssignReviewerFormFields.reviewerAssignments, assignments, {
                shouldValidate: false,
                shouldDirty: true,
            });
            onChange();
        },
        [setValue, onChange],
    );

    const addReviewers = useCallback(
        ({ revieweeId, reviewers: reviewersToAdd }: { revieweeId: string; reviewers: User[] }) => {
            if (!reviewersToAdd.length) {
                return;
            }

            const previousAssignments =
                getValues(AssignReviewerFormFields.reviewerAssignments) || [];
            const existingEntry = previousAssignments.find(
                (assignment) => assignment.revieweeId === revieweeId,
            );

            const combinedReviewers = existingEntry ? [...existingEntry.reviewers] : [];

            reviewersToAdd.forEach((reviewer) => {
                const alreadyAssigned = combinedReviewers.some(
                    (assigned) => assigned.id === reviewer.id,
                );
                if (!alreadyAssigned) {
                    combinedReviewers.push(reviewer);
                }
            });

            if (combinedReviewers.length > MAX_NODES) {
                showWarningToast({
                    title: t('evaluation360AssignReviewers.validation.title'),
                    message: t('evaluation360AssignReviewers.validation.maxReviewers', {
                        count: MAX_NODES,
                    }),
                });
                return;
            }

            const updatedAssignments = previousAssignments.map((assignment) =>
                assignment.revieweeId === revieweeId
                    ? { ...assignment, reviewers: combinedReviewers }
                    : assignment,
            );

            if (!existingEntry) {
                const reviewee = baseReviewees.find((r) => r.id === revieweeId);
                updatedAssignments.push({
                    revieweeId,
                    reviewers: combinedReviewers,
                    reviewAudienceAssignmentId: reviewee?.reviewAudienceAssignmentId ?? '',
                });
            }

            saveLocally(updatedAssignments);
        },
        [baseReviewees, getValues, saveLocally, t],
    );

    const removeReviewer = useCallback(
        ({ revieweeId, reviewerId }: { revieweeId: string; reviewerId: string }) => {
            const previousAssignments =
                getValues(AssignReviewerFormFields.reviewerAssignments) || [];

            saveLocally(
                previousAssignments.map((assignment) =>
                    assignment.revieweeId === revieweeId
                        ? {
                              ...assignment,
                              reviewers: assignment.reviewers.filter(
                                  (reviewer: User) => reviewer.id !== reviewerId,
                              ),
                          }
                        : assignment,
                ),
            );
        },
        [getValues, saveLocally],
    );

    return { addReviewers, removeReviewer };
};
